import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Check, Menu, X, ArrowRight, Star, FileText, CreditCard, 
  Users, Calendar, Shield
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const trialFeatures = [
  { icon: FileText, text: 'Unlimited contracts & invoices' },
  { icon: Calendar, text: 'Event & booking management' },
  { icon: Users, text: 'Client portal access' },
  { icon: CreditCard, text: 'Payment processing' },
  { icon: Shield, text: 'E-signatures' },
  { icon: Star, text: 'All premium features' },
];

export default function PublicFreeTrialPage() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    company_name: '',
    email: '',
    password: '',
    name: ''
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/frontend-settings/public`);
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Use the existing signup endpoint
      const response = await axios.post(`${API}/auth/signup`, {
        email: formData.email,
        password: formData.password,
        name: formData.name,
        company_name: formData.company_name
      });

      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        toast.success('Account created! Welcome to your free trial.');
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Signup error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create account');
    } finally {
      setSubmitting(false);
    }
  };

  const seo = settings?.seo || {};
  const trial = settings?.free_trial || {};
  const navLinks = [
    { label: 'Features', href: '/features' },
    { label: 'FAQ', href: '/faq' },
    { label: 'Contact', href: '/contact' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-lg border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="font-bold text-xl text-gray-900">
              {seo.site_title || 'DJ CRM'}
            </Link>
            <div className="hidden md:flex items-center gap-8">
              {navLinks.map(link => (
                <Link 
                  key={link.href}
                  to={link.href}
                  className="text-gray-600 hover:text-gray-900 font-medium transition-colors"
                >
                  {link.label}
                </Link>
              ))}
            </div>
            <div className="hidden md:flex items-center gap-4">
              <Link to="/auth">
                <Button variant="ghost" className="text-gray-700">Login</Button>
              </Link>
            </div>
            <button className="md:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 px-4 py-4 space-y-4">
            {navLinks.map(link => (
              <Link key={link.href} to={link.href} className="block text-gray-600" onClick={() => setMobileMenuOpen(false)}>
                {link.label}
              </Link>
            ))}
            <div className="pt-4 border-t border-gray-100">
              <Link to="/auth" className="block"><Button variant="outline" className="w-full">Login</Button></Link>
            </div>
          </div>
        )}
      </nav>

      {/* Main Content */}
      <section className="pt-24 pb-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left - Benefits */}
            <div className="order-2 lg:order-1">
              <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
                {trial.heading || 'Start Your Free Trial'}
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                {trial.subheadline || `Try all features free for ${trial.trial_days || 14} days. No credit card required.`}
              </p>

              {trial.show_features_list !== false && (
                <div className="space-y-4">
                  <p className="font-semibold text-gray-900">What's included:</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {trialFeatures.map((feature, index) => {
                      const Icon = feature.icon;
                      return (
                        <div key={index} className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                            <Check className="w-4 h-4 text-green-600" />
                          </div>
                          <span className="text-gray-700">{feature.text}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="mt-8 p-4 bg-blue-50 rounded-xl border border-blue-100">
                <p className="text-blue-800 text-sm">
                  <strong>No credit card required.</strong> Start your {trial.trial_days || 14}-day free trial 
                  instantly. Cancel anytime.
                </p>
              </div>
            </div>

            {/* Right - Form */}
            <div className="order-1 lg:order-2">
              <div className="bg-gray-50 rounded-2xl p-8 border border-gray-100 shadow-sm">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Create your account</h2>
                
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-gray-700">Your Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      required
                      placeholder="John Smith"
                      className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="company" className="text-gray-700">Company / DJ Name *</Label>
                    <Input
                      id="company"
                      value={formData.company_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, company_name: e.target.value }))}
                      required
                      placeholder="DJ Awesome Entertainment"
                      className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-gray-700">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      required
                      placeholder="you@example.com"
                      className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-gray-700">Password *</Label>
                    <Input
                      id="password"
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                      required
                      placeholder="Min 8 characters"
                      minLength={8}
                      className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    disabled={submitting}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-lg"
                  >
                    {submitting ? (
                      'Creating account...'
                    ) : (
                      <>
                        Start Free Trial
                        <ArrowRight className="ml-2 w-5 h-5" />
                      </>
                    )}
                  </Button>
                </form>

                <p className="text-center text-sm text-gray-500 mt-6">
                  Already have an account?{' '}
                  <Link to="/auth" className="text-blue-600 hover:underline">
                    Login here
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12 px-4">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="text-center md:text-left">
            <span className="font-bold text-xl text-white">{seo.site_title || 'DJ CRM'}</span>
            <p className="mt-2 text-sm">{settings?.footer_text || '© 2024 DJ CRM. All rights reserved.'}</p>
          </div>
          <div className="flex items-center gap-6">
            {navLinks.map(link => (
              <Link key={link.href} to={link.href} className="text-gray-400 hover:text-white text-sm">{link.label}</Link>
            ))}
            <Link to="/auth" className="text-gray-400 hover:text-white text-sm">Login</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
